/**
 * vue-sparklines v2.0.1
 * (c) 2022 tangzhen@me.com
 * @license MIT
 */

import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { defineComponent, ref, computed, provide, h, unref, nextTick } from 'vue';
import { sparklineProps } from './props.js';
import debounce from './utils/debounce.js';
import './utils/uid.js';
import './constant.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it["return"] != null) it["return"](); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
var curEvt = {};
var Base = defineComponent({
  name: "Sparklines",
  props: sparklineProps,
  setup: function setup(props2) {
    var isFocusing = ref(false);
    var indicator = ref(null);
    var tooltip = ref(null);
    var pointDatum = ref({});
    var svgMouseEvt = ref(true);
    var execMouseEvt = computed(function () {
      if (!props2.indicatorStyles || !indicator.value || !svgMouseEvt.value) {
        return false;
      }

      return true;
    });
    var styles = computed(function () {
      var styles2 = props2.styles;
      styles2.width = props2.width;
      styles2.height = props2.height;
      return styles2;
    });

    var setStatus = function setStatus() {
      var status = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      var display = status ? "" : "none";
      tooltip.value && (tooltip.value.style.display = display);
      indicator.value && (indicator.value.style.display = display);
    };

    var updateData = function updateData() {
      if (!isFocusing.value) {
        return false;
      }

      var rect;
      var curData = null;
      var tooltipContent = "";
      var pd = unref(pointDatum.value);

      for (var datum in pd) {
        curData = null;

        if (pd.hasOwnProperty(datum)) {
          setStatus(false);

          var _iterator = _createForOfIteratorHelper(pd[datum].points.entries()),
              _step;

          try {
            for (_iterator.s(); !(_step = _iterator.n()).done;) {
              var _step$value = _slicedToArray(_step.value, 2),
                  i = _step$value[0],
                  p = _step$value[1];

              if (curEvt.ox < p.x && curData === null) {
                var _tooltip$value, _tooltip$value$getBou;

                setStatus();
                rect = (_tooltip$value = tooltip.value) === null || _tooltip$value === void 0 ? void 0 : (_tooltip$value$getBou = _tooltip$value.getBoundingClientRect) === null || _tooltip$value$getBou === void 0 ? void 0 : _tooltip$value$getBou.call(_tooltip$value);
                curData = {
                  value: pd[datum].data[i],
                  color: pd[datum].color,
                  index: i
                };
                tooltipContent += "<span style=\"color: ".concat(curData.color, ";\">&bull;</span>&nbsp;").concat(curData.value, "<br />");
              }
            }
          } catch (err) {
            _iterator.e(err);
          } finally {
            _iterator.f();
          }
        }
      }

      if (rect) {
        tooltip.value.style.left = "".concat(curEvt.cx + rect.width / 2, "px");
        tooltip.value.style.top = "".concat(curEvt.cy - props2.height - rect.height, "px");

        try {
          var _props2$tooltipProps, _props2$tooltipProps$;

          tooltip.value.innerHTML = ((_props2$tooltipProps = props2.tooltipProps) === null || _props2$tooltipProps === void 0 ? void 0 : (_props2$tooltipProps$ = _props2$tooltipProps.formatter) === null || _props2$tooltipProps$ === void 0 ? void 0 : _props2$tooltipProps$.call(_props2$tooltipProps, curData)) || tooltipContent;
        } catch (err) {
          tooltip.value.innerHTML = tooltipContent;
        }
      }
    };

    var updateTooltip = debounce(updateData, props2.tooltipDelay);

    var updateValue = function updateValue(value) {
      var id = value.id,
          data = value.data,
          points = value.points,
          color = value.color,
          limit = value.limit;
      nextTick(function () {
        pointDatum.value[id] = {
          data: data.length >= limit ? data.slice(-limit) : data,
          points: points,
          color: color
        };
        Object.keys(curEvt).length && updateTooltip();
      });
    };

    provide("eventBus", {
      tooltip: tooltip,
      setStatus: setStatus,
      updateValue: updateValue,
      svgMouseEvt: svgMouseEvt,
      isFocusing: isFocusing
    });
    return {
      styles: styles,
      updateData: updateData,
      isFocusing: isFocusing,
      setStatus: setStatus,
      tooltip: tooltip,
      indicator: indicator,
      updateTooltip: updateTooltip,
      execMouseEvt: execMouseEvt
    };
  },
  render: function render() {
    var _this = this;

    var width = this.width,
        height = this.height,
        preserveAspectRatio = this.preserveAspectRatio,
        styles = this.styles,
        indicatorStyles = this.indicatorStyles,
        tooltipStyles = this.tooltipStyles,
        setStatus = this.setStatus,
        updateTooltip = this.updateTooltip;
    return h("div", {
      "class": "sparkline"
    }, [h("svg", {
      style: styles,
      viewBox: "0 0 ".concat(width, " ").concat(height),
      preserveAspectRatio: preserveAspectRatio,
      onMousemove: function onMousemove(evt) {
        if (!_this.execMouseEvt) {
          return;
        }

        var ox = evt.offsetX;
        var oy = evt.offsetY;
        var cx = evt.clientX;
        var cy = evt.clientY;
        curEvt = {
          ox: ox,
          oy: oy,
          cx: cx,
          cy: cy,
          target: evt.target
        };

        _this.indicator.setAttribute("x1", "".concat(ox));

        _this.indicator.setAttribute("x2", "".concat(ox));

        _this.isFocusing = true;
        updateTooltip();
      },
      onMouseleave: function onMouseleave() {
        _this.isFocusing = false;
        setStatus(false);
      }
    }, function () {
      var _this$$slots, _this$$slots$default;

      var items = ((_this$$slots = _this.$slots) === null || _this$$slots === void 0 ? void 0 : (_this$$slots$default = _this$$slots["default"]) === null || _this$$slots$default === void 0 ? void 0 : _this$$slots$default.call(_this$$slots)) || [];

      if (typeof indicatorStyles !== "boolean") {
        indicatorStyles["shape-rendering"] = "crispEdges";
        indicatorStyles["display"] = "none";
        items.push(h("line", {
          style: indicatorStyles,
          x1: 0,
          y1: 0,
          x2: 0,
          y2: height,
          ref: "indicator"
        }));
      }

      return items;
    }()), h("div", {
      "class": "sparkline__tooltip",
      style: _objectSpread(_objectSpread({}, tooltipStyles), {}, {
        position: "fixed"
      }),
      ref: "tooltip"
    })]);
  }
});

export { Base as default };
//# sourceMappingURL=Sparklines.js.map
